/**
 * @module ol/layer/WebGLTile
 */
import {ColorType, NumberType} from '../expr/expression.js';
import {
  getStringNumberEquivalent,
  newCompilationContext,
  PALETTE_TEXTURE_ARRAY,
  uniformNameForVariable,
} from '../expr/gpu.js';
import LayerProperty from '../layer/Property.js';
import {equivalent} from '../proj.js';
import {expressionToGlsl} from '../render/webgl/compileUtil.js';
import WebGLTileLayerRenderer, {
  Attributes,
  Uniforms,
} from '../renderer/webgl/TileLayer.js';
import BaseTileLayer from './BaseTile.js';

/**
 * @typedef {import("../source/DataTile.js").default<import("../DataTile.js").default|import("../ImageTile.js").default>} SourceType
 */

/**
 * @typedef {Object} Style
 * Translates tile data to rendered pixels.
 *
 * @property {Object<string, (string|number)>} [variables] Style variables.  Each variable must hold a number or string.  These
 * variables can be used in the `color`, `brightness`, `contrast`, `exposure`, `saturation` and `gamma`
 * {@link import("../expr/expression.js").ExpressionValue expressions}, using the `['var', 'varName']` operator.
 * To update style variables, use the {@link import("./WebGLTile.js").default#updateStyleVariables} method.
 * @property {import("../expr/expression.js").ExpressionValue} [color] An expression applied to color values.
 * @property {import("../expr/expression.js").ExpressionValue} [brightness=0] Value used to decrease or increase
 * the layer brightness.  Values range from -1 to 1.
 * @property {import("../expr/expression.js").ExpressionValue} [contrast=0] Value used to decrease or increase
 * the layer contrast.  Values range from -1 to 1.
 * @property {import("../expr/expression.js").ExpressionValue} [exposure=0] Value used to decrease or increase
 * the layer exposure.  Values range from -1 to 1.
 * @property {import("../expr/expression.js").ExpressionValue} [saturation=0] Value used to decrease or increase
 * the layer saturation.  Values range from -1 to 1.
 * @property {import("../expr/expression.js").ExpressionValue} [gamma=1] Apply a gamma correction to the layer.
 * Values range from 0 to infinity.
 */

/**
 * @typedef {Object} Options
 * @property {Style} [style] Style to apply to the layer.
 * @property {string} [className='ol-layer'] A CSS class name to set to the layer element.
 * @property {number} [opacity=1] Opacity (0, 1).
 * @property {boolean} [visible=true] Visibility.
 * @property {import("../extent.js").Extent} [extent] The bounding extent for layer rendering.  The layer will not be
 * rendered outside of this extent.
 * @property {number} [zIndex] The z-index for layer rendering.  At rendering time, the layers
 * will be ordered, first by Z-index and then by position. When `undefined`, a `zIndex` of 0 is assumed
 * for layers that are added to the map's `layers` collection, or `Infinity` when the layer's `setMap()`
 * method was used.
 * @property {number} [minResolution] The minimum resolution (inclusive) at which this layer will be
 * visible.
 * @property {number} [maxResolution] The maximum resolution (exclusive) below which this layer will
 * be visible.
 * @property {number} [minZoom] The minimum view zoom level (exclusive) above which this layer will be
 * visible.
 * @property {number} [maxZoom] The maximum view zoom level (inclusive) at which this layer will
 * be visible.
 * @property {number} [preload=0] Preload. Load low-resolution tiles up to `preload` levels. `0`
 * means no preloading.
 * @property {SourceType} [source] Source for this layer.
 * @property {Array<SourceType>|function(import("../extent.js").Extent, number):Array<SourceType>} [sources] Array
 * of sources for this layer. Takes precedence over `source`. Can either be an array of sources, or a function that
 * expects an extent and a resolution (in view projection units per pixel) and returns an array of sources. See
 * {@link module:ol/source.sourcesFromTileGrid} for a helper function to generate sources that are organized in a
 * pyramid following the same pattern as a tile grid. **Note:** All sources must have the same band count and content.
 * @property {import("../Map.js").default} [map] Sets the layer as overlay on a map. The map will not manage
 * this layer in its layers collection, and the layer will be rendered on top. This is useful for
 * temporary layers. The standard way to add a layer to a map and have it managed by the map is to
 * use {@link module:ol/Map~Map#addLayer}.
 * @property {boolean} [useInterimTilesOnError=true] Deprecated.  Use interim tiles on error.
 * @property {number} [cacheSize=512] The internal texture cache size.  This needs to be large enough to render
 * two zoom levels worth of tiles.
 * @property {Object<string, *>} [properties] Arbitrary observable properties. Can be accessed with `#get()` and `#set()`.
 */

/**
 * @typedef {Object} ParsedStyle
 * @property {string} vertexShader The vertex shader.
 * @property {string} fragmentShader The fragment shader.
 * @property {Object<string,import("../webgl/Helper.js").UniformValue>} uniforms Uniform definitions.
 * @property {Array<import("../webgl/PaletteTexture.js").default>} paletteTextures Palette textures.
 */

/**
 * @param {Style} style The layer style.
 * @param {number} [bandCount] The number of bands.
 * @param {number} [nodataBandIndex] The 1-based band index for the nodata alpha band.
 * @return {ParsedStyle} Shaders and uniforms generated from the style.
 */
function parseStyle(style, bandCount, nodataBandIndex) {
  const vertexShader = `
    attribute vec2 ${Attributes.TEXTURE_COORD};
    uniform mat4 ${Uniforms.TILE_TRANSFORM};
    uniform float ${Uniforms.TEXTURE_PIXEL_WIDTH};
    uniform float ${Uniforms.TEXTURE_PIXEL_HEIGHT};
    uniform float ${Uniforms.TEXTURE_RESOLUTION};
    uniform float ${Uniforms.DEPTH};

    varying vec2 v_textureCoord;
    varying vec2 v_localMapCoord;

    void main() {
      v_textureCoord = ${Attributes.TEXTURE_COORD};
      v_localMapCoord = vec2(
        ${Uniforms.TEXTURE_PIXEL_WIDTH} * ${Uniforms.TEXTURE_RESOLUTION} * v_textureCoord[0],
        -1. * ${Uniforms.TEXTURE_PIXEL_HEIGHT} * ${Uniforms.TEXTURE_RESOLUTION} * v_textureCoord[1]
      );
      gl_Position = ${Uniforms.TILE_TRANSFORM} * vec4(${Attributes.TEXTURE_COORD}, ${Uniforms.DEPTH}, 1.0);
    }
  `;

  /**
   * @type {import("../expr/gpu.js").CompilationContext}
   */
  const context = {
    ...newCompilationContext(),
    bandCount: bandCount,
  };

  const pipeline = [];

  if (style.color !== undefined) {
    const color = expressionToGlsl(context, style.color, ColorType);
    pipeline.push(`color = ${color};`);
  }

  if (style.contrast !== undefined) {
    const contrast = expressionToGlsl(context, style.contrast, NumberType);
    pipeline.push(
      `color.rgb = clamp((${contrast} + 1.0) * color.rgb - (${contrast} / 2.0), vec3(0.0, 0.0, 0.0), vec3(1.0, 1.0, 1.0));`,
    );
  }

  if (style.exposure !== undefined) {
    const exposure = expressionToGlsl(context, style.exposure, NumberType);
    pipeline.push(
      `color.rgb = clamp((${exposure} + 1.0) * color.rgb, vec3(0.0, 0.0, 0.0), vec3(1.0, 1.0, 1.0));`,
    );
  }

  if (style.saturation !== undefined) {
    const saturation = expressionToGlsl(context, style.saturation, NumberType);
    pipeline.push(`
      float saturation = ${saturation} + 1.0;
      float sr = (1.0 - saturation) * 0.2126;
      float sg = (1.0 - saturation) * 0.7152;
      float sb = (1.0 - saturation) * 0.0722;
      mat3 saturationMatrix = mat3(
        sr + saturation, sr, sr,
        sg, sg + saturation, sg,
        sb, sb, sb + saturation
      );
      color.rgb = clamp(saturationMatrix * color.rgb, vec3(0.0, 0.0, 0.0), vec3(1.0, 1.0, 1.0));
    `);
  }

  if (style.gamma !== undefined) {
    const gamma = expressionToGlsl(context, style.gamma, NumberType);
    pipeline.push(`color.rgb = pow(color.rgb, vec3(1.0 / ${gamma}));`);
  }

  if (style.brightness !== undefined) {
    const brightness = expressionToGlsl(context, style.brightness, NumberType);
    pipeline.push(
      `color.rgb = clamp(color.rgb + ${brightness}, vec3(0.0, 0.0, 0.0), vec3(1.0, 1.0, 1.0));`,
    );
  }

  /** @type {Object<string,import("../webgl/Helper.js").UniformValue>} */
  const uniforms = {};

  const numVariables = context.variables.size;
  if (numVariables > 1 && !style.variables) {
    throw new Error(
      `Missing variables in style (expected ${Array.from(context.variables.keys())})`,
    );
  }

  const styleVariables = style.variables || {};

  for (const [variableName] of context.variables.entries()) {
    if (!(variableName in styleVariables)) {
      throw new Error(`Missing '${variableName}' in style variables`);
    }
    const uniformName = uniformNameForVariable(variableName);
    uniforms[uniformName] = function () {
      let value = styleVariables[variableName];
      if (typeof value === 'string') {
        value = getStringNumberEquivalent(value);
      }
      return value !== undefined ? value : -9999999; // to avoid matching with the first string literal
    };
  }

  const uniformDeclarations = Object.keys(uniforms).map(function (name) {
    return `uniform float ${name};`;
  });

  const bandCountValue = bandCount !== undefined ? bandCount : 4;
  const textureCount = Math.ceil(bandCountValue / 4);
  uniformDeclarations.push(
    `uniform sampler2D ${Uniforms.TILE_TEXTURE_ARRAY}[${textureCount}];`,
  );

  if (context.paletteTextures) {
    uniformDeclarations.push(
      `uniform sampler2D ${PALETTE_TEXTURE_ARRAY}[${context.paletteTextures.length}];`,
    );
  }

  // Ensure getBandValue function exists when nodata discard is needed
  if (
    nodataBandIndex &&
    nodataBandIndex > 0 &&
    !('getBandValue' in context.functions)
  ) {
    let ifBlocks = '';
    for (let i = 0; i < bandCountValue; i++) {
      const colorIndex = Math.floor(i / 4);
      let bandIndex = i % 4;
      if (i === bandCountValue - 1 && bandIndex === 1) {
        // LUMINANCE_ALPHA - band 1 assigned to rgb and band 2 assigned to alpha
        bandIndex = 3;
      }
      const textureName = `${Uniforms.TILE_TEXTURE_ARRAY}[${colorIndex}]`;
      ifBlocks += `  if (band == ${i + 1}.0) {\n    return texture2D(${textureName}, v_textureCoord + vec2(dx, dy))[${bandIndex}];\n  }\n`;
    }
    context.functions['getBandValue'] =
      `float getBandValue(float band, float xOffset, float yOffset) {\n  float dx = xOffset / ${Uniforms.TEXTURE_PIXEL_WIDTH};\n  float dy = yOffset / ${Uniforms.TEXTURE_PIXEL_HEIGHT};\n${ifBlocks}\n}`;
  }

  const functionDefintions = Object.keys(context.functions).map(
    function (name) {
      return context.functions[name];
    },
  );

  const fragmentShader = `
    #ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    #else
    precision mediump float;
    #endif

    varying vec2 v_textureCoord;
    varying vec2 v_localMapCoord;
    uniform vec4 ${Uniforms.RENDER_EXTENT};
    uniform float ${Uniforms.TRANSITION_ALPHA};
    uniform float ${Uniforms.TEXTURE_PIXEL_WIDTH};
    uniform float ${Uniforms.TEXTURE_PIXEL_HEIGHT};
    uniform float ${Uniforms.RESOLUTION};
    uniform float ${Uniforms.ZOOM};

    ${uniformDeclarations.join('\n')}

    ${functionDefintions.join('\n')}

    void main() {
      if (
        v_localMapCoord[0] < ${Uniforms.RENDER_EXTENT}[0] ||
        v_localMapCoord[1] < ${Uniforms.RENDER_EXTENT}[1] ||
        v_localMapCoord[0] > ${Uniforms.RENDER_EXTENT}[2] ||
        v_localMapCoord[1] > ${Uniforms.RENDER_EXTENT}[3]
      ) {
        discard;
      }

      vec4 color = texture2D(${
        Uniforms.TILE_TEXTURE_ARRAY
      }[0],  v_textureCoord);

      ${nodataBandIndex && style.color === undefined ? `color.a = getBandValue(${nodataBandIndex}.0, 0.0, 0.0);` : ''}

      ${nodataBandIndex ? `if (getBandValue(${nodataBandIndex}.0, 0.0, 0.0) == 0.0) { discard; }` : ''}

      ${pipeline.join('\n')}

      gl_FragColor = color;
      gl_FragColor.rgb *= gl_FragColor.a;
      gl_FragColor *= ${Uniforms.TRANSITION_ALPHA};
    }`;

  return {
    vertexShader: vertexShader,
    fragmentShader: fragmentShader,
    uniforms: uniforms,
    paletteTextures: context.paletteTextures || [],
  };
}

/**
 * @classdesc
 * For layer sources that provide pre-rendered, tiled images in grids that are
 * organized by zoom levels for specific resolutions.
 * Note that any property set in the options is set as a {@link module:ol/Object~BaseObject}
 * property on the layer object; for example, setting `title: 'My Title'` in the
 * options means that `title` is observable, and has get/set accessors.
 *
 * @extends BaseTileLayer<SourceType, WebGLTileLayerRenderer<WebGLTileLayer>>
 * @fires import("../render/Event.js").RenderEvent#prerender
 * @fires import("../render/Event.js").RenderEvent#postrender
 * @api
 */
class WebGLTileLayer extends BaseTileLayer {
  /**
   * @param {Options} [options] Tile layer options.
   */
  constructor(options) {
    options = options ? Object.assign({}, options) : {};

    const style = options.style || {};
    delete options.style;

    super(options);

    /**
     * @type {Array<SourceType>|function(import("../extent.js").Extent, number):Array<SourceType>|undefined}
     * @private
     */
    this.sources_ =
      /** @type {Array<SourceType>|function(import("../extent.js").Extent, number):Array<SourceType>|undefined} */ (
        options.sources
      );

    /**
     * @type {SourceType|null}
     * @private
     */
    this.renderedSource_ = null;

    /**
     * @type {number}
     * @private
     */
    this.renderedResolution_ = NaN;

    /**
     * @type {Style}
     * @private
     */
    this.style_ = style;

    /**
     * @type {Object<string, (string|number)>}
     * @private
     */
    this.styleVariables_ = this.style_.variables || {};

    /**
     * The band count the shaders were last built for (may include a coverage
     * band added when reprojecting an alpha-less source).
     * @type {number}
     * @private
     */
    this.styleBandCount_ = NaN;

    /**
     * The nodata band index the shaders were last built for.
     * @type {number|undefined}
     * @private
     */
    this.styleNodataBandIndex_ = undefined;

    this.handleSourceUpdate_();
    this.addChangeListener(LayerProperty.SOURCE, this.handleSourceUpdate_);
  }

  /**
   * Gets the sources for this layer, for a given extent and resolution.
   * @param {import("../extent.js").Extent} extent Extent.
   * @param {number} resolution Resolution.
   * @return {Array<SourceType>} Sources.
   */
  getSources(extent, resolution) {
    const source = this.getSource();
    if (this.sources_) {
      return typeof this.sources_ === 'function'
        ? this.sources_(extent, resolution) || []
        : this.sources_;
    }
    return source ? [source] : [];
  }

  /**
   * @return {SourceType|null} The source being rendered.
   * @override
   */
  getRenderSource() {
    return this.renderedSource_ || this.getSource();
  }

  /**
   * @return {import("../source/Source.js").State} Source state.
   * @override
   */
  getSourceState() {
    const source = this.getRenderSource();
    return source ? source.getState() : 'undefined';
  }

  /**
   * @private
   */
  handleSourceUpdate_() {
    if (this.hasRenderer()) {
      this.getRenderer()?.clearCache();
    }
    const source = this.getSource();
    if (source) {
      if (source.getState() === 'loading') {
        const onChange = () => {
          if (source.getState() === 'ready') {
            source.removeEventListener('change', onChange);
            this.setStyle(this.style_);
          }
        };
        source.addEventListener('change', onChange);
      } else {
        this.setStyle(this.style_);
      }
    }
  }

  /**
   * @private
   * @return {SourceType|null} The first render source (or null).
   */
  getFirstSource_() {
    const max = Number.MAX_SAFE_INTEGER;
    const sources = this.getSources([-max, -max, max, max], max);
    return sources && sources.length ? sources[0] : null;
  }

  /**
   * Whether reprojecting the source to the given projection appends a coverage
   * alpha band (only for sources that do not already carry an alpha band).
   * @private
   * @param {SourceType} source The render source.
   * @param {import("../proj/Projection.js").default} [projection] The render projection.
   * @return {boolean} A coverage band is added.
   */
  usesCoverageBand_(source, projection) {
    if (!source || !projection || source.hasAlpha !== false) {
      return false;
    }
    const sourceProjection = source.getProjection();
    return !!sourceProjection && !equivalent(sourceProjection, projection);
  }

  /**
   * @private
   * @param {import("../proj/Projection.js").default} [projection] The render projection.
   * @return {number} The number of source bands.
   */
  getSourceBandCount_(projection) {
    const source = this.getFirstSource_();
    const bandCount = source && 'bandCount' in source ? source.bandCount : 4;
    return this.usesCoverageBand_(
      /** @type {SourceType} */ (source),
      projection,
    )
      ? bandCount + 1
      : bandCount;
  }

  /**
   * @private
   * @param {import("../proj/Projection.js").default} [projection] The render projection.
   * @return {number|undefined} The 1-based band index for the nodata alpha band.
   */
  getSourceNodataBandIndex_(projection) {
    const source = this.getFirstSource_();
    if (!source) {
      return undefined;
    }
    if (this.usesCoverageBand_(source, projection)) {
      // The appended coverage band is the last (1-based) band.
      return source.bandCount + 1;
    }
    return 'nodataBandIndex' in source ? source.nodataBandIndex : undefined;
  }

  /**
   * Parse the style for the given render projection, tracking the band layout
   * used.  The render projection determines whether a coverage band is added
   * for reprojected alpha-less sources.
   * @private
   * @param {import("../proj/Projection.js").default} [projection] The render projection.
   * @return {ReturnType<typeof parseStyle>} The parsed style.
   */
  parseStyleForRender_(projection) {
    const bandCount = this.getSourceBandCount_(projection);
    const nodataBandIndex = this.getSourceNodataBandIndex_(projection);
    this.styleBandCount_ = bandCount;
    this.styleNodataBandIndex_ = nodataBandIndex;
    return parseStyle(this.style_, bandCount, nodataBandIndex);
  }

  /**
   * Rebuild the shaders for the given render projection and apply them to the
   * renderer.
   * @private
   * @param {import("../proj/Projection.js").default} [projection] The render projection.
   */
  applyShaders_(projection) {
    const parsedStyle = this.parseStyleForRender_(projection);
    this.getRenderer()?.reset({
      vertexShader: parsedStyle.vertexShader,
      fragmentShader: parsedStyle.fragmentShader,
      uniforms: parsedStyle.uniforms,
      paletteTextures: parsedStyle.paletteTextures,
    });
  }

  /**
   * @override
   */
  createRenderer() {
    const projection = this.getMapInternal()?.getView()?.getProjection();
    const parsedStyle = this.parseStyleForRender_(projection);

    return new WebGLTileLayerRenderer(this, {
      vertexShader: parsedStyle.vertexShader,
      fragmentShader: parsedStyle.fragmentShader,
      uniforms: parsedStyle.uniforms,
      cacheSize: this.getCacheSize(),
      paletteTextures: parsedStyle.paletteTextures,
    });
  }

  /**
   * @param {import("../Map.js").FrameState} frameState Frame state.
   * @param {Array<SourceType>} sources Sources.
   * @return {HTMLElement} Canvas.
   */
  renderSources(frameState, sources) {
    const layerRenderer = this.getRenderer();
    if (!layerRenderer) {
      return /** @type {HTMLElement} */ (document.createElement('div'));
    }
    let canvas;
    for (let i = 0, ii = sources.length; i < ii; ++i) {
      this.renderedSource_ = sources[i];
      if (layerRenderer.prepareFrame(frameState)) {
        canvas = layerRenderer.renderFrame(frameState);
      }
    }
    return canvas || /** @type {HTMLElement} */ (document.createElement('div'));
  }

  /**
   * @param {?import("../Map.js").FrameState} frameState Frame state.
   * @param {HTMLElement} target Target which the renderer may (but need not) use
   * for rendering its content.
   * @return {HTMLElement} The rendered element.
   * @override
   */
  render(frameState, target) {
    this.rendered = true;
    if (!frameState) {
      return /** @type {HTMLElement} */ (document.createElement('div'));
    }
    const viewState = frameState.viewState;
    const extent = frameState.extent;
    if (!extent) {
      return /** @type {HTMLElement} */ (document.createElement('div'));
    }

    // Reprojecting an alpha-less source appends a coverage band, changing the
    // band layout the shaders must read.  Rebuild them when that changes (e.g.
    // the first reprojected frame, or after a view projection change).
    if (this.hasRenderer()) {
      const projection = viewState.projection;
      if (
        this.getSourceBandCount_(projection) !== this.styleBandCount_ ||
        this.getSourceNodataBandIndex_(projection) !==
          this.styleNodataBandIndex_
      ) {
        this.applyShaders_(projection);
      }
    }

    const sources = this.getSources(extent, viewState.resolution);
    let ready = true;
    for (let i = 0, ii = sources.length; i < ii; ++i) {
      const source = sources[i];
      const sourceState = source.getState();
      if (sourceState == 'loading') {
        const onChange = () => {
          if (source.getState() == 'ready') {
            source.removeEventListener('change', onChange);
            this.changed();
          }
        };
        source.addEventListener('change', onChange);
      }
      ready = ready && sourceState == 'ready';
    }
    const canvas = this.renderSources(frameState, sources);
    const renderer = this.getRenderer();
    if (renderer?.renderComplete && ready) {
      // Fully rendered, done.
      this.renderedResolution_ = viewState.resolution;
      return canvas;
    }
    // Render sources from previously fully rendered frames
    if (this.renderedResolution_ > 0.5 * viewState.resolution) {
      const altSources = this.getSources(
        extent,
        this.renderedResolution_,
      ).filter((source) => !sources.includes(source));
      if (altSources.length > 0) {
        return this.renderSources(frameState, altSources);
      }
    }
    return canvas;
  }

  /**
   * Update the layer style.  The `updateStyleVariables` function is a more efficient
   * way to update layer rendering.  In cases where the whole style needs to be updated,
   * this method may be called instead.  Note that calling this method will also replace
   * any previously set variables, so the new style also needs to include new variables,
   * if needed.
   * @param {Style} style The new style.
   */
  setStyle(style) {
    this.styleVariables_ = style.variables || {};
    this.style_ = style;
    if (this.hasRenderer()) {
      const projection = this.getMapInternal()?.getView()?.getProjection();
      this.applyShaders_(projection);
      this.changed();
    }
  }

  /**
   * Update any variables used by the layer style and trigger a re-render.
   * @param {Object<string, number>} variables Variables to update.
   * @api
   */
  updateStyleVariables(variables) {
    Object.assign(this.styleVariables_, variables);
    this.changed();
  }
}

/**
 * Clean up underlying WebGL resources.
 * @function
 * @api
 */
WebGLTileLayer.prototype.dispose;

export default WebGLTileLayer;
